// supabase/functions/am-copilot/time.ts
//
// ONE Ask AI tool for Time & Labor (Ben, 2026-10-26, item #982, demo approved
// the same day; TCSD-0531 one tool per part). Its one action today records a
// missed shift by hand ("William's punch link did not work last night. He
// worked St. Peters 8:30 to 11:45, add that shift"). The card saves through the
// function the Timecards screen calls (manualSegment → rpc_time_manual_segment,
// src/modules/time-labor/hooks/use-time-data.ts); the frontend half is
// src/modules/copilot/time-actions.ts.
//
// Who may record where is the database's rule (TCSD-0553): HR, an executive, a
// superuser, or an account manager at a building is_my_facility() admits (their
// own, every building for the national AM). A shift booked to a market is HR's
// and is not drafted here.
//
// Pure: no Deno, no database — vitest runs it (src/modules/copilot/ask-ai-time.test.ts).

// @ts-ignore TS5097 — Deno needs the .ts extension; the app's tsc does not allow it.
import { canReviewTime, hasFullAmAuthority, isAmRole, type RoleLike } from "./gates.ts";

export const TIME_ACTIONS = ["record_shift"] as const;
export type TimeAction = (typeof TIME_ACTIONS)[number];

/** The words a person uses for the day, read on the building's clock. */
export const DAY_WORDS = ["today", "yesterday", "last night"] as const;
const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The input the model sent, checked the way the Timecards form would. The refusal in words, or null. */
export function validateTimeActionInput(input: Record<string, unknown>): string | null {
  const action = input.action as TimeAction;
  if (!TIME_ACTIONS.includes(action)) return `action must be one of ${TIME_ACTIONS.join(", ")}`;
  if (typeof input.worker_id !== "string" || !/^WRK-\d{3,6}$/.test(input.worker_id)) {
    return "worker_id must be a worker id as stored (WRK-0123) — find it with query_database on v_time_worker_picker by name";
  }
  if (input.facility_id != null && (typeof input.facility_id !== "string" || !/^FAC-\d{3,6}$/.test(input.facility_id))) {
    return "facility_id must be a building id as stored (FAC-0123) from lookup_facility, or left out to use the worker's usual building";
  }
  if (typeof input.day !== "string" || resolveDayWord(input.day, "2000-01-03") === null) {
    return `day must be YYYY-MM-DD, a weekday name, or one of ${DAY_WORDS.join(", ")}`;
  }
  for (const k of ["in_time", "out_time"] as const) {
    if (typeof input[k] !== "string" || !HHMM.test(input[k] as string)) return `${k} must be a 24-hour time, HH:MM (8:30 PM is 20:30)`;
  }
  if (input.in_time === input.out_time) return "the in and out times are the same";
  if (input.reason != null && (typeof input.reason !== "string" || input.reason.length > 500)) return "reason must be words, under 500 characters";
  return null;
}

/** Add days to a YYYY-MM-DD. */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * The day the shift started, on the building's calendar. `today` is the
 * building's date now. "Last night" and "yesterday" are the day before; a
 * weekday name is the most recent such day (today if it is that day); a
 * YYYY-MM-DD is itself. Null when it cannot be read.
 */
export function resolveDayWord(word: string, today: string): string | null {
  const w = word.trim().toLowerCase();
  if (ISO_DAY.test(w)) return w;
  if (w === "today" || w === "tonight") return today;
  if (w === "yesterday" || w === "last night") return addDays(today, -1);
  const wd = WEEKDAYS.indexOf(w.replace(/^last /, ""));
  if (wd >= 0) {
    const now = new Date(`${today}T12:00:00Z`).getUTCDay();
    return addDays(today, -((now - wd + 7) % 7));
  }
  return null;
}

/** A shift whose out time is not after its in time ends the next morning. */
export function crossesMidnight(inTime: string, outTime: string): boolean {
  return outTime <= inTime;
}

/** Hours between two HH:MM, across midnight when out is earlier. */
export function shiftHours(inTime: string, outTime: string): number {
  const m = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  let d = m(outTime) - m(inTime);
  if (d <= 0) d += 1440;
  return Math.round((d / 60) * 100) / 100;
}

/**
 * Why this person may not record a shift at this building — the save's own rule
 * in words — or null when the card can be offered. `amName` names the building's
 * account manager so the answer says who can.
 */
export function timeActionRefusal(r: RoleLike, eid: string | null, buildingAmEid: string | null, amName: string | null): string | null {
  if (canReviewTime(r)) return null;
  if (isAmRole(r) && (hasFullAmAuthority(r) || (!!eid && buildingAmEid === eid))) return null;
  if (isAmRole(r)) {
    return `that building isn't one of yours, so you can't record a shift there. Its account manager is ${amName ?? "someone else"} — they or HR can record it`;
  }
  return "recording a shift by hand is done by HR, an executive or the building's account manager";
}
